import { ref } from 'vue';
import { uploadRefImage, addAsset, imageCompressor, readFileAsBase64 } from '@/lib/asset';
import { getToken } from './useApi';
import { jwtDecode } from 'jwt-decode';

function normalizeProgress(p) {
  if (typeof p === 'number') return Math.max(0, Math.min(100, p));
  if (typeof p === 'string') return Math.max(0, Math.min(100, Number(p) || 0));
  if (!p || typeof p !== 'object') return 0;

  if ('progress' in p) {
    const n = Number(p.progress);
    return Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));
  }

  if ('loaded' in p && 'total' in p) {
    const loaded = Number(p.loaded);
    const total = Number(p.total);
    if (Number.isFinite(loaded) && Number.isFinite(total) && total > 0) {
      return Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
    }
  }

  if ('lengthComputable' in p && p.lengthComputable && 'total' in p && 'loaded' in p) {
    const loaded = Number(p.loaded);
    const total = Number(p.total);
    if (Number.isFinite(loaded) && Number.isFinite(total) && total > 0) {
      return Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
    }
  }

  return 0;
}

function getUser() {
  const token = getToken();
  if (!token) {
    console.error('No token found');
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    return {
      id: decoded.id,
      name: decoded.name || '',
      email: decoded.email,
      token: token,
    };
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
}

export function useAttachmentUpload() {
  const isUploading = ref({
    reference: false,
    material: false,
    document: false,
  });

  const uploadProgress = ref({
    reference: 0,
    material: 0,
    document: 0,
  });

  function validateFile(file, type, onError) {
    const maxSizes = {
      reference: 15 * 1024 * 1024,
      material: 15 * 1024 * 1024,
      document: 30 * 1024 * 1024,
    };

    const allowedTypes = {
      reference: ['image/png', 'image/jpeg', 'image/webp'],
      material: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
      document: [
        'text/plain',
        'application/pdf',
        'text/markdown',
        'text/x-markdown',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
    };

    const maxSize = maxSizes[type] || 30 * 1024 * 1024;

    if (file.size > maxSize) {
      onError?.('File size exceeds limit');
      return false;
    }

    const isValidType = allowedTypes[type].some((allowedType) => {
      if (type === 'document') {
        return (
          file.type === allowedType ||
          file.name.toLowerCase().endsWith('.txt') ||
          file.name.toLowerCase().endsWith('.md') ||
          file.name.toLowerCase().endsWith('.doc') ||
          file.name.toLowerCase().endsWith('.docx')
        );
      }
      return file.type === allowedType;
    });

    if (!isValidType) {
      onError?.('Unsupported file format');
      return false;
    }

    return true;
  }

  async function uploadReference(file, callbacks = {}) {
    if (!file || !validateFile(file, 'reference', callbacks.onError)) return;

    isUploading.value.reference = true;
    uploadProgress.value.reference = 0;

    try {
      await uploadRefImage(
        file,
        (payload) => {
          callbacks.onSuccess?.(payload);
        },
        (p) => {
          uploadProgress.value.reference = normalizeProgress(p);
        }
      );
    } catch (error) {
      console.error('Failed to upload reference image:', error);
      const errorMsg = error?.message || 'Upload failed';
      callbacks.onError?.(errorMsg);
    } finally {
      isUploading.value.reference = false;
      uploadProgress.value.reference = 0;
    }
  }

  async function uploadMaterial(files, callbacks = {}) {
    const validFiles = Array.from(files).filter((file) =>
      validateFile(file, 'material', callbacks.onError)
    );
    if (validFiles.length === 0) return;

    isUploading.value.material = true;
    uploadProgress.value.material = 0;

    const useImmediateMode = typeof callbacks.onUpdate === 'function';

    try {
      const user = getUser();
      if (!user) {
        throw new Error('User not authenticated, cannot upload material');
      }

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileStartPercent = (i / validFiles.length) * 100;
        const filePercentSpan = 100 / validFiles.length;

        let tempId = null;
        let localUrl = null;

        if (useImmediateMode) {
          tempId = `temp_${Date.now()}_${i}`;
          localUrl = URL.createObjectURL(file);
          const tempAsset = {
            id: tempId,
            url: localUrl,
            filename: file.name,
            isMaterial: true,
            loading: true,
            progress: 0,
            uploadStatus: 'uploading',
          };
          callbacks.onPick?.(tempAsset);
        } else if (Array.isArray(callbacks.tempIds) && callbacks.tempIds[i]) {
          tempId = String(callbacks.tempIds[i]);
        }

        uploadProgress.value.material = fileStartPercent;
        callbacks.onProgress?.({
          tempId,
          index: i,
          total: validFiles.length,
          progress: 0,
          overallProgress: uploadProgress.value.material,
          stage: 'compress',
          file,
        });
        const img = await imageCompressor(file, { maxSize: 1920 });

        uploadProgress.value.material = fileStartPercent + filePercentSpan * 0.5;
        callbacks.onProgress?.({
          tempId,
          index: i,
          total: validFiles.length,
          progress: 0,
          overallProgress: uploadProgress.value.material,
          stage: 'upload',
          file,
        });

        const asset = await addAsset(
          img.dataURL,
          { filename: file.name, source: 'material' },
          (p) => {
            const progress = normalizeProgress(p);
            uploadProgress.value.material = fileStartPercent + filePercentSpan * (progress / 100);
            callbacks.onProgress?.({
              tempId,
              index: i,
              total: validFiles.length,
              progress,
              overallProgress: uploadProgress.value.material,
              stage: 'upload',
              file,
            });
          }
        );

        if (useImmediateMode) {
          callbacks.onUpdate(tempId, {
            ...asset,
            isMaterial: true,
            loading: false,
            progress: 100,
            uploadStatus: 'done',
          });
          URL.revokeObjectURL(localUrl);
        } else {
          callbacks.onPick?.(asset);
        }

        uploadProgress.value.material = fileStartPercent + filePercentSpan;
        callbacks.onProgress?.({
          tempId,
          index: i,
          total: validFiles.length,
          progress: 100,
          overallProgress: uploadProgress.value.material,
          stage: 'done',
          file,
        });
      }
      callbacks.onSuccess?.();
    } catch (error) {
      console.error('Failed to upload material:', error);
      const errorMsg = error?.message || 'Upload failed';
      callbacks.onError?.(errorMsg);
    } finally {
      isUploading.value.material = false;
      uploadProgress.value.material = 0;
    }
  }

  async function uploadDocument(files, callbacks = {}) {
    const validFiles = Array.from(files).filter((file) =>
      validateFile(file, 'document', callbacks.onError)
    );
    if (validFiles.length === 0) return;

    isUploading.value.document = true;
    uploadProgress.value.document = 0;

    try {
      const user = getUser();
      if (!user) {
        throw new Error('User not authenticated, cannot upload document');
      }

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileStartPercent = (i / validFiles.length) * 100;
        const filePercentSpan = 100 / validFiles.length;

        uploadProgress.value.document = fileStartPercent + filePercentSpan * 0.2;
        const text = await readFileAsBase64(file);
        uploadProgress.value.document = fileStartPercent + filePercentSpan * 0.5;

        const asset = await addAsset(text, { filename: file.name, source: 'document' }, (p) => {
          const progress = normalizeProgress(p);
          uploadProgress.value.document = fileStartPercent + filePercentSpan * (progress / 100);
        });

        callbacks.onPick?.(asset);
        uploadProgress.value.document = fileStartPercent + filePercentSpan;
      }
      callbacks.onSuccess?.();
    } catch (error) {
      console.error('Failed to upload document:', error);
      const errorMsg = error?.message || 'Upload failed';
      callbacks.onError?.(errorMsg);
    } finally {
      isUploading.value.document = false;
      uploadProgress.value.document = 0;
    }
  }

  return {
    isUploading,
    uploadProgress,
    uploadReference,
    uploadMaterial,
    uploadDocument,
    validateFile,
  };
}
